import toastr from "toastr";

export const MAX_SHIFT_PATTERNS = 3;

export const experienceLevelOptions = [
  { value: "New to Sector", label: "New to Sector" },
  { value: "Developing Experience", label: "Developing Experience" },
  { value: "Competent", label: "Competent" },
  { value: "Highly Experienced", label: "Highly Experienced" },
];

export const maxShiftPatternsForBooking = (isUrgent) =>
  isUrgent ? 1 : MAX_SHIFT_PATTERNS;

export const emptySupervisor = () => ({
  source: "new",
  supervisorSlug: "",
  name: "",
  mobile: "",
  email: "",
});

export function mapSupervisorToPayload(s, withPlus) {
  if (s.source === "existing" && s.supervisorSlug) {
    return { supervisorSlug: s.supervisorSlug };
  }
  return {
    name: s.name,
    mobile: withPlus(s.mobile),
    email: s.email,
  };
}

export function mapSupervisorFromApi(s) {
  const supervisorSlug = s?.supervisorSlug || s?.slug;
  if (supervisorSlug) {
    return {
      source: "existing",
      supervisorSlug,
      name: s?.fullName || s?.name || "",
      mobile: String(s?.phoneNumber || s?.mobile || "").replace(/^\+/, ""),
      email: s?.email || "",
    };
  }
  return {
    source: "new",
    supervisorSlug: "",
    name: s?.fullName || s?.name || "",
    mobile: String(s?.phoneNumber || s?.mobile || "").replace(/^\+/, ""),
    email: s?.email || "",
  };
}

export function supervisorIdentityKey(s) {
  if (!s) return null;
  if (s.source === "existing" && s.supervisorSlug) {
    return `slug:${s.supervisorSlug}`;
  }
  if (s.source === "new") {
    const email = String(s.email || "")
      .trim()
      .toLowerCase();
    if (email) return `email:${email}`;
    const mobile = String(s.mobile || "").replace(/\D/g, "");
    if (mobile.length >= 10) return `mobile:${mobile}`;
  }
  return null;
}

export function isDuplicateSupervisorInShift(supervisors, candidateIdx) {
  const key = supervisorIdentityKey(supervisors[candidateIdx]);
  if (!key) return false;
  return supervisors.some(
    (s, i) => i !== candidateIdx && supervisorIdentityKey(s) === key
  );
}

export function supervisorsFromYupContext(from) {
  if (!from?.length) return [];
  for (let i = from.length - 1; i >= 0; i--) {
    const value = from[i]?.value;
    if (Array.isArray(value?.supervisor)) return value.supervisor;
  }
  return [];
}

export const BOOKING_STEP_FIELD_PREFIXES = {
  1: [
    "hasPORequired",
    "poReference",
    "poDocument",
    "timezone",
    "location",
  ],
  2: ["isUrgent", "shiftPattern"],
  3: ["agreements"],
};

export function setFormikErrorAtPath(target, path, message) {
  if (!path) return;
  const parts = path.replace(/\[(\d+)\]/g, ".$1").split(".").filter(Boolean);
  let cur = target;
  for (let i = 0; i < parts.length - 1; i++) {
    const key = parts[i];
    const nextKey = parts[i + 1];
    if (cur[key] == null || typeof cur[key] !== "object") {
      cur[key] = /^\d+$/.test(nextKey) ? [] : {};
    }
    cur = cur[key];
  }
  cur[parts[parts.length - 1]] = message;
}

export function yupValidationErrorToFormik(error) {
  const formikErrors = {};
  if (!error) return formikErrors;
  if (error.inner?.length) {
    for (const issue of error.inner) {
      if (issue.path) setFormikErrorAtPath(formikErrors, issue.path, issue.message);
    }
  } else if (error.path) {
    setFormikErrorAtPath(formikErrors, error.path, error.message);
  }
  return formikErrors;
}

export function flattenFormikErrorPaths(errors, prefix = "") {
  if (!errors) return [];
  if (typeof errors === "string") return prefix ? [prefix] : [];
  if (Array.isArray(errors)) {
    return errors.flatMap((item, i) =>
      flattenFormikErrorPaths(item, prefix ? `${prefix}.${i}` : String(i))
    );
  }
  if (typeof errors !== "object") return [];
  return Object.entries(errors).flatMap(([key, val]) => {
    const path = prefix ? `${prefix}.${key}` : key;
    if (typeof val === "string") return [path];
    return flattenFormikErrorPaths(val, path);
  });
}

export function errorPathBelongsToStep(path, step) {
  const prefixes = BOOKING_STEP_FIELD_PREFIXES[step] || [];
  return prefixes.some(
    (p) => path === p || path.startsWith(`${p}.`) || path.startsWith(`${p}[`)
  );
}

export function mergeStepErrors(previousErrors, step, stepErrors) {
  const merged = { ...(previousErrors || {}) };
  for (const key of Object.keys(merged)) {
    if ((BOOKING_STEP_FIELD_PREFIXES[step] || []).includes(key)) {
      delete merged[key];
    }
  }
  return { ...merged, ...stepErrors };
}

export function setFormikLocationErrors(formik, locationFieldErrors) {
  const next = { ...formik.errors };
  if (!locationFieldErrors || Object.keys(locationFieldErrors).length === 0) {
    delete next.location;
  } else {
    next.location = locationFieldErrors;
  }
  formik.setErrors(next);
}

export async function validateFormikLocation(formik, locationSchema, locationValues) {
  try {
    await locationSchema.validate(locationValues, { abortEarly: false });
    setFormikLocationErrors(formik, null);
    return true;
  } catch (err) {
    if (err.name === "ValidationError") {
      const locationErrors = {};
      if (err.inner?.length) {
        for (const issue of err.inner) {
          if (issue.path) locationErrors[issue.path] = issue.message;
        }
      } else if (err.path) {
        locationErrors[err.path] = err.message;
      }
      setFormikLocationErrors(formik, locationErrors);
    }
    return false;
  }
}

export function getStepTouched(step, values) {
  if (step === 1) {
    return {
      hasPORequired: true,
      poReference: true,
      poDocument: true,
      timezone: true,
      location: {
        siteName: true,
        fullAddress: true,
        latitude: true,
        longitude: true,
        searchAddress: true,
        accessNotes: true,
      },
      notes: true,
    };
  }
  if (step === 2) {
    return {
      isUrgent: true,
      shiftPattern: (values.shiftPattern || []).map((p) => ({
        sectorSlug: true,
        roleSlug: true,
        quantity: true,
        hasPPEProvided: true,
        ppeInstruction: true,
        startDate: true,
        endDate: true,
        excludeWeekend: true,
        excludeSpecificDates: true,
        dates: true,
        startTime: true,
        endTime: true,
        breakTime: true,
        experienceLevel: true,
        certSlugs: true,
        shiftDetails: true,
        supervisor: (p.supervisor || []).map(() => ({
          source: true,
          supervisorSlug: true,
          name: true,
          mobile: true,
          email: true,
        })),
      })),
    };
  }
  if (step === 3) {
    return {
      agreements: {
        confirmAccurate: true,
        qrCheckin: true,
        hnsResponsibility: true,
        terms: true,
      },
    };
  }
  return {};
}

function friendlyStepErrorLabel(path) {
  const map = {
    poReference: "Purchase Order Reference",
    poDocument: "Purchase Order Document",
    "location.siteName": "Site Name",
    "location.fullAddress": "Full Address",
    "location.latitude": "Location Latitude",
    "location.longitude": "Location Longitude",
    timezone: "Timezone",
    "agreements.confirmAccurate": "Confirm information is accurate",
    "agreements.qrCheckin": "QR check-in agreement",
    "agreements.hnsResponsibility": "Health & safety responsibility",
    "agreements.terms": "Terms and conditions",
  };
  if (map[path]) return map[path];
  const patternMatch = path.match(/^shiftPattern\.(\d+)\.(.+)$/);
  if (patternMatch) {
    const idx = Number(patternMatch[1]) + 1;
    const field = patternMatch[2].replace(/\.\d+\./g, " ").replace(/\./g, " ");
    return `Shift Pattern ${idx}: ${field}`;
  }
  return path;
}

export function toastStepValidationErrors(step, stepErrors) {
  const paths = flattenFormikErrorPaths(stepErrors).filter((p) =>
    errorPathBelongsToStep(p, step)
  );
  if (!paths.length) {
    toastr.error("Please complete the required fields before continuing.");
    return;
  }
  const labels = [...new Set(paths.map(friendlyStepErrorLabel))];
  if (step === 1) {
    toastr.error(
      `Please complete the required fields: ${labels.slice(0, 6).join(", ")}${
        labels.length > 6 ? "…" : ""
      }`
    );
    return;
  }
  toastr.error(
    `Please fix validation errors: ${labels.slice(0, 4).join(", ")}${
      labels.length > 4 ? "…" : ""
    }`
  );
}

export function isBookingShiftEditable(status) {
  return String(status || "").toLowerCase() === "pending";
}

export function buildShiftPatternPayloadEntry(p, generateDatesArray) {
  return {
    sectorSlug: p.sectorSlug,
    roleSlug: p.roleSlug,
    quantity: Number(p.quantity),
    hasPPEProvided: p.hasPPEProvided === "yes",
    ...(p.hasPPEProvided === "yes" ? { ppeInstruction: p.ppeInstruction } : {}),
    dates: generateDatesArray(
      p.startDate,
      p.endDate,
      p.excludeWeekend,
      p.excludeSpecificDates
    ),
    startTime: p.startTime,
    endTime: p.endTime,
    breakTime: p.breakTime,
    shiftDetails: p.shiftDetails,
    experienceLevel: Array.isArray(p.experienceLevel) ? p.experienceLevel : [],
    ...(p.certSlugs?.length ? { certSlugs: p.certSlugs } : {}),
  };
}
